import axios from 'axios';
import { BACKEND_MAIN_URL } from '../config';
import { projectAuthHeaders } from '../projectAuth';
import { acknowledgeNote, acknowledgeProjectInstructions, getProject, listProjects } from './projectRepository';

const contextSyncs = new Map();

export async function syncProjectContext(userId, projectId) {
  const account = String(userId || '');
  if (!account || navigator.onLine === false) return 0;
  const key = JSON.stringify([account, String(projectId)]);
  if (contextSyncs.has(key)) return contextSyncs.get(key);
  const sync = (async () => {
    let count = 0;
    while (navigator.onLine !== false && localStorage.getItem('userId') === account) {
      const project = await getProject(account, projectId);
      const pending = project?.pendingInstructions;
      if (!pending) break;
      const { data } = await axios.patch(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(projectId)}`,
        { instructions: pending.value }, { headers: projectAuthHeaders() });
      await acknowledgeProjectInstructions(account, projectId, pending.revision, data);
      window.dispatchEvent(new CustomEvent('upya-project-context-synced', { detail: { userId: account, projectId } }));
      count += 1;
    }
    return count;
  })().finally(() => contextSyncs.delete(key));
  contextSyncs.set(key, sync);
  return sync;
}

export async function syncPendingProjectContexts(userId) {
  if (!userId || navigator.onLine === false) return 0;
  const projects = await listProjects(userId);
  const results = await Promise.allSettled(projects.filter((project) => project.pendingInstructions)
    .map((project) => syncProjectContext(userId, project.id)));
  const failed = results.filter((result) => result.status === 'rejected').length;
  if (failed) throw new Error(`${failed} project contexts remain pending`);
  return results.reduce((sum, result) => sum + (result.status === 'fulfilled' ? result.value : 0), 0);
}

// Safe to repeat after process death: PUT uses the note's stable client ID.
export async function syncPendingProjectNotes(userId) {
  const projects = await listProjects(userId);
  let synced = 0;
  let failed = 0;
  for (const project of projects) {
    for (const note of (project.notes || []).filter((item) => item.pending)) {
      try {
        const { data } = await axios.put(`${BACKEND_MAIN_URL}/api/projects/${encodeURIComponent(project.id)}/notes/${encodeURIComponent(note.id)}`,
          { text: note.text }, { headers: projectAuthHeaders() });
        await acknowledgeNote(userId, project.id, data);
        synced += 1;
      } catch { failed += 1; }
    }
  }
  if (failed) throw new Error(`${failed} notes remain pending`);
  return synced;
}
